import type { ReactNode } from 'react';

enum FormFieldSchemaType {
  // Can setup custom types.
  Textarea = 'textarea',
  Checkbox = 'checkbox',
  Radio = 'radio',
  Select = 'select',
  Search = 'search',
  Number = 'number',
  Email = 'email',
  Text = 'text',
  Date = 'date',
  Time = 'time',
  Tel = 'tel',
  Url = 'url',
}

enum ConditionalOperatorPrimitive {
  Equal = 'equal',
  NotEqual = 'not-equal',
  GreaterThanOrEqual = 'greater-than-or-equal',
  LessThanOrEqual = 'less-than-or-equal',
  GreaterThan = 'greater-than',
  LessThan = 'less-than',
}

enum ConditionalOperatorArray {
  In = 'in',
  NotIn = 'not-in',
  Includes = 'includes',
  NotIncludes = 'not-includes',
}

enum ValueFormatter {
  Percentage = 'percentage',
}

// @TODO: Make this even more stricter based on 'type'.
interface SelectFieldOption<TValue = string | number> {
  label: string;
  value: TValue;
  description?: string;
}

type PrimitiveValue = string | number;

/**
 * Current field value is compared against the provided 'value' (field-to-value comparison).
 * Another 'field' cannot be provided, since information is always linked to current field.
 */
type InformationIsVisibleWhenCondition =
  | {
      value: PrimitiveValue;
      operator: ConditionalOperatorPrimitive;
    }
  | {
      value: PrimitiveValue[];
      operator: ConditionalOperatorArray;
    };

/**
 * Watched 'field' value is compared against the provided 'value' (field-to-value comparison).
 * 'value' cannot be omitted, since current field's value is 'undefined' when it is not visible.
 */
type FormFieldIsVisibleWhenCondition =
  | {
      field: string;
      value: PrimitiveValue;
      operator: ConditionalOperatorPrimitive;
    }
  | {
      field: string;
      value: PrimitiveValue[];
      operator: ConditionalOperatorArray;
    };

/**
 * 'value' is provided - Watched 'field' value is compared against the provided 'value' (field-to-value comparison).
 * 'value' is omitted - Current field's value is compared against watched 'field' value (field-to-field comparison).
 */
type FormFieldIsValidWhenCondition =
  | {
      field: string;
      value?: PrimitiveValue;
      operator: ConditionalOperatorPrimitive;
      message: string; // Custom error message.
    }
  | {
      field: string;
      value?: PrimitiveValue[];
      operator: ConditionalOperatorArray;
      message: string; // Custom error message.
    };

type OptionsPositioning = 'inline' | 'stacked';

type InformationPosition = 'after' | 'before';

type WidthVariant = 'full' | '1/2-block' | '1/2-inline';

type WatchedFormValues = Record<string, FormFieldSchema['value']>;

interface FieldInformation {
  title?: string;
  description?: ReactNode;
  position?: InformationPosition; // Defaults to 'after'.
  /**
   * Determines if field information is visible or not.
   * All given conditions must pass (AND-ed together).
   */
  isVisibleWhen?: InformationIsVisibleWhenCondition[];
}

interface FieldLayoutOptions {
  width?: WidthVariant; // Defaults to 'full'.
  hasSeparator?: boolean; // Defaults to 'false'.
  isVisuallyHidden?: boolean; // Defaults to 'false'.
  isLabelInvisible?: boolean; // Defaults to 'false'.
  optionsPositioning?: OptionsPositioning; // Defaults to 'stacked'.
}

// @TODO: Make this even more stricter based on 'type'.
interface FormFieldSchema<TValue = string | number> {
  /* Required schema attributes. */
  type: FormFieldSchemaType;
  name: string;

  /* Optional schema attributes. */
  // Not passed to the field element.
  title?: string;
  text?: string;

  // Passed to the form field element.
  label?: string;
  description?: ReactNode;
  placeholder?: string;

  // Used as default value on load.
  value?: TValue | TValue[];

  // Used for showing information.
  information?: FieldInformation;

  /* Conditional schema attributes. */
  isRequired?: boolean; // Defaults to true.
  isDisabled?: boolean; // Defaults to false.
  minLength?: number; // Defaults to 'undefined'.
  maxLength?: number; // Defaults to 'undefined'.

  // Layout options for field (and its elements).
  layoutOptions?: FieldLayoutOptions;

  /**
   * Determines if the form field is visible or not.
   * All given conditions must pass (AND-ed together).
   */
  isVisibleWhen?: FormFieldIsVisibleWhenCondition[];

  /**
   * Additional custom validation for this form field.
   * All given conditions must pass (AND-ed together).
   */
  isValidWhen?: FormFieldIsValidWhenCondition[];

  // Only used for selectable fields.
  options?: SelectFieldOption<TValue>[];

  // Custom value formatter for review section. See formatFieldValue for details.
  valueFormatter?: ValueFormatter;

  // Can be used to setup options to add or remove repeatable fields from the form.
  repeatOptions?:
    | {
        action: 'add'; // Repeatable action type.
        label: string; // The label for the button.
        // The values that are added in given order.
        values: SelectFieldOption<string>['value'][];
      }
    | {
        action: 'remove'; // Repeatable action type.
        label: string; // The label for the button.
        value: string; // The value to be removed.
        // The field from which value is removed.
        field: FormFieldSchema<string>['name'];
      };
}

interface FormStepSchema {
  // Required schema attributes.
  name: string;
  title: string;

  // The form fields in this step.
  form: {
    name: string;
    fields: (FormFieldSchema<string> | FormFieldSchema<number>)[];
  };

  /**
   * Timestamp using 'Date.now()'.
   * Can be used for memoization.
   */
  lastUpdated: number;

  // Optional schema attributes.
  description?: ReactNode;

  // Used with step progress bar.
  titleShort?: string;
}

type MultiStepFormSchema = FormStepSchema[];

export {
  ConditionalOperatorArray,
  ConditionalOperatorPrimitive,
  FormFieldSchemaType,
  ValueFormatter,
};

export type {
  FieldInformation,
  FieldLayoutOptions,
  FormFieldIsValidWhenCondition,
  FormFieldIsVisibleWhenCondition,
  FormFieldSchema,
  FormStepSchema,
  InformationIsVisibleWhenCondition,
  MultiStepFormSchema,
  OptionsPositioning,
  PrimitiveValue,
  SelectFieldOption,
  WatchedFormValues,
};
